/**
 * The approval page appended to an approved document.
 *
 * The signature block is authored per organization by an admin, so the wording
 * lives in configuration rather than in code. It is shared here because the
 * admin UI renders a live preview and the API renders the real page: if the two
 * substituted differently, the preview would lie about a compliance artifact.
 */

/** Placeholders an admin may use in the signature template. */
export const APPROVAL_PLACEHOLDERS = [
  "organization",
  "reviewId",
  "reviewTitle",
  "productName",
  "channel",
  "decision",
  "reviewer",
  "reviewerEmail",
  "approvedAt",
] as const;

export type ApprovalPlaceholder = (typeof APPROVAL_PLACEHOLDERS)[number];

export type ApprovalPlaceholderValues = Record<ApprovalPlaceholder, string>;

export const APPROVAL_SIGNATURE_TEMPLATE_MAX_LENGTH = 2000;

/**
 * Korean is the product's default language and every current tenant is a Korean
 * insurer, so the shipped default is Korean. Admins may replace it entirely.
 */
export const DEFAULT_APPROVAL_SIGNATURE_TEMPLATE = [
  "본 광고물은 컴플라이언스세이지(ComplianceSage)를 통해 심의,승인되었습니다.",
  "",
  "심의 결과: {{decision}}",
  "심의자: {{reviewer}} ({{reviewerEmail}})",
  "승인 일시: {{approvedAt}}",
  "확인필 번호: {{reviewId}}",
  "",
  "{{organization}} 준법감시인 확인필",
].join("\n");

/**
 * Labels written into `{{decision}}`. The enum codes themselves (APPROVED)
 * must never appear on a Korean approval page.
 */
export const APPROVAL_DECISION_LABELS = {
  ko: {
    APPROVED: "승인완료",
    APPROVED_WITH_CHANGES: "조건부 승인",
    REJECTED: "반려",
    SELF_CHECK_COMPLETE: "자체점검 완료",
  },
  en: {
    APPROVED: "Approved",
    APPROVED_WITH_CHANGES: "Approved with Changes",
    REJECTED: "Rejected",
    SELF_CHECK_COMPLETE: "Self-check complete",
  },
} as const;

export type ApprovalDecisionLang = keyof typeof APPROVAL_DECISION_LABELS;

/** Display label for a stamp decision. Korean is the document default. */
export function approvalDecisionLabel(
  decision: string | null | undefined,
  lang: ApprovalDecisionLang = "ko",
): string {
  const key = decision ?? "SELF_CHECK_COMPLETE";
  const labels = APPROVAL_DECISION_LABELS[lang] as Record<string, string>;
  return labels[key] ?? key;
}

/** Matches `{{name}}`, tolerating inner whitespace. */
const PLACEHOLDER_PATTERN = /\{\{\s*([A-Za-z]+)\s*\}\}/g;

/**
 * Placeholders an admin typed that we cannot fill. Reported when the template is
 * saved: a `{{reviewrr}}` typo that reached a signed document would be far worse
 * than a rejected save.
 */
export function unknownApprovalPlaceholders(template: string): string[] {
  const known = new Set<string>(APPROVAL_PLACEHOLDERS);
  const unknown = new Set<string>();
  for (const match of template.matchAll(PLACEHOLDER_PATTERN)) {
    const name = match[1]!;
    if (!known.has(name)) unknown.add(name);
  }
  return [...unknown];
}

/** Substitutes every known placeholder. Validation rejects unknown ones on save. */
export function renderApprovalTemplate(
  template: string,
  values: ApprovalPlaceholderValues,
): string {
  return template.replace(PLACEHOLDER_PATTERN, (whole, name: string) =>
    name in values ? values[name as ApprovalPlaceholder] : whole,
  );
}
